import { PermissionType } from '@cbinsights/adminpermissionservice/adminpermissionservice';
import { Col, Row, Spin, Typography } from 'antd';
import AddFeedCreditsButton from 'client/modules/data-feed-credit/components/AddFeedCreditsButton';
import CreateTeamBalanceButton from 'client/modules/data-feed-credit/components/CreateTeamBalanceButton';
import { useGetTeamFeedBalance } from 'client/modules/data-feed-credit/hooks/queries/data-feed-credit';
import { dataFeedPermissions } from 'client/modules/data-feed-credit/permissions';
import useHasAccess from 'client/utils/permissions-validation/hooks/useHasAccess';
import moment from 'moment';
import React, { useMemo } from 'react';

type Props = {
  idTeam: number;
  name: string;
};

type Timestamp = {
  nano: number;
  seconds: string;
};

const formatDate = (timestamp: unknown) => {
  const unixMsTimestamp = Number((timestamp as Timestamp)?.seconds || 0) * 1000;
  return unixMsTimestamp > 0
    ? moment.utc(unixMsTimestamp).format('MM-DD-YYYY')
    : '';
};

export const DataFeedCredit = ({ idTeam, name }: Props) => {
  const hasAccessToDataFeedCredit = useHasAccess({
    pageId: dataFeedPermissions.pageId,
    permissionType: PermissionType.PUBLISH,
  });

  const { data: teamBalance, isLoading } = useGetTeamFeedBalance(idTeam);

  const team = useMemo(() => ({ idTeam, name }), [idTeam, name]);

  if (!hasAccessToDataFeedCredit) {
    return (
      <div className="text-center">
        You do not have permission to view this tab.
      </div>
    );
  }

  if (isLoading) {
    return <Spin />;
  }

  if (!teamBalance) {
    return (
      <>
        <p className="font-medium">No existing data feed credit balance.</p>
        <CreateTeamBalanceButton className="mt-3" team={team} />
      </>
    );
  }

  return (
    <Row>
      <Col
        xs={{ span: 24 }}
        md={{ span: 24 }}
        lg={{ span: 8 }}
        xxl={{ span: 8 }}
      >
        <Row justify="space-between" className="mb-3">
          <Col span="12">
            <Typography.Title level={5} className="!font-medium">
              ID
            </Typography.Title>
          </Col>
          <Col span="12" className="text-right">
            {teamBalance.name}
          </Col>
        </Row>
        <Row justify="space-between" className="mb-3">
          <Col span="12">
            <Typography.Title level={5} className="!font-medium">
              Current Credits:
            </Typography.Title>
          </Col>
          <Col span="12" className="text-right">
            {teamBalance.current_balance}
          </Col>
        </Row>
        <Row justify="space-between" className="mb-3">
          <Col span="12">
            <Typography.Title level={5} className="!font-medium">
              Created:
            </Typography.Title>
          </Col>
          <Col span="12" className="text-right">
            {formatDate(teamBalance.create_date_time)}
          </Col>
        </Row>
        <Row justify="space-between" className="mb-3">
          <Col span="12">
            <Typography.Title level={5} className="!font-medium">
              Last Updated:
            </Typography.Title>
          </Col>
          <Col span="12" className="text-right">
            {formatDate(teamBalance.last_update_date_time)}
          </Col>
        </Row>

        <AddFeedCreditsButton
          className="mt-4"
          team={team}
          teamBalance={teamBalance}
        />
      </Col>
    </Row>
  );
};

export default DataFeedCredit;
